import { useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LocalizedLink } from "@/components/LocalizedLink";
import { getShownPlanError, setShownPlanError, subscribePlanError } from "@/hooks/usePlanGate";
import { useLanguage } from "@/hooks/useLanguage";
import { useOrganization } from "@/hooks/useOrganization";
import { notOwnerNote, upgradeCopy, type PlanError } from "@/lib/billing/entitlements";

/** Renders the upgrade dialog; mounted once, inside the router (App.tsx). */
export function PlanGateHost() {
  const error = useSyncExternalStore(subscribePlanError, getShownPlanError);
  return <UpgradeDialog error={error} onClose={() => setShownPlanError(null)} />;
}

export function UpgradeDialog({
  error,
  onClose,
}: {
  error: PlanError | null;
  onClose: () => void;
}) {
  const { language } = useLanguage();
  const locale = language === "en" ? "en" : "cs";
  const { currentOrganization } = useOrganization();
  const isOwner = currentOrganization?.role === "owner";
  const copy = error ? upgradeCopy(error, locale) : null;

  return (
    <Dialog open={!!error} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        {copy && (
          <>
            <DialogHeader>
              <DialogTitle>{copy.title}</DialogTitle>
              <DialogDescription className="tabular-nums">{copy.body}</DialogDescription>
            </DialogHeader>
            {!isOwner && (
              <p className="text-[13px] text-muted-foreground">{notOwnerNote(locale)}</p>
            )}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={onClose}>
                {locale === "cs" ? "Zavřít" : "Close"}
              </Button>
              {isOwner && copy.cta && (
                <Button asChild onClick={onClose}>
                  <LocalizedLink to="/settings/billing">{copy.cta}</LocalizedLink>
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
